import { FC, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import TextArea from 'antd/lib/input/TextArea'

import PerText from '../Text'
import PerSpacer from '../Spacer'
import PerFlexRow from '../FlexRow'
import PerButtonCircle from '../ButtonCircle'
import { useAuth } from '../../../context/auth-context'
import { SmartAvatar } from '../../../modules/avatar/SmartAvatar'
import { useDateTime } from '../../../shared/useDateTime'
import { NoteInfoInputExtended } from './types'
import { useTargetTemplateModalStore } from './TargetTemplateModalStore'

interface TargetTemplateModalCommentProps {
  comment: NoteInfoInputExtended
  index?: number
}

export const TargetTemplateModalComment: FC<TargetTemplateModalCommentProps> = ({ comment, index }) => {
  const { user } = useAuth()
  const { t } = useTranslation()
  const { formatDateShort, isToday } = useDateTime(user)

  const [input, setInput] = useState<string>('')
  const [commentEditable, toggleCommentEditable] = useState<boolean>(false)
  const [removeComment, editComment] = useTargetTemplateModalStore((state) => [state.removeComment, state.editComment], shallow)

  const handleEditComment = () => {
    editComment(comment.id, input.trim())
    toggleCommentEditable(false)
  }

  useEffect(() => {
    setInput(comment.text)
  }, [comment])

  return (
    <PerFlexRow align="top" className="per-newNote" noWrap mt={index === 0 ? 'm' : undefined} mb="s">
      <PerFlexRow align="middle" className="per-newNote--user per-target-template-comments--user-wrapper" noWrap>
        <SmartAvatar
          employeeId={comment.author.id}
          firstName={comment.author.firstName ? comment.author.firstName : undefined}
          lastName={comment.author.lastName ? comment.author.lastName : undefined}
        />
        <PerSpacer ml="xs" mr="l" className="per-target-template-comments--user">
          <PerText fontWeight="bold" className="per-target-template-comments--user-name">
            {comment.author.firstName}
          </PerText>
          <PerText appearance="flowing-text" color="secondary">
            {isToday(comment.date) ? t('global.today') : formatDateShort(comment.date)}
          </PerText>
        </PerSpacer>
      </PerFlexRow>
      <PerFlexRow align="top" justify="start" noWrap className="per-target-template-comments--input" ml="s">
        {!commentEditable && !comment.isNewComment && (
          <div className="per-noteItem--text" data-e2e-noteslist>
            <PerText appearance="flowing-text">{comment.text}</PerText>
          </div>
        )}
        {!commentEditable && !!comment.isNewComment && (
          <div className="per-noteItem--text" data-e2e-noteslist onClick={() => toggleCommentEditable(true)}>
            <PerText appearance="flowing-text">{comment.text}</PerText>
          </div>
        )}
        {!!comment.isNewComment && commentEditable && (
          <>
            <TextArea
              name="text"
              placeholder="Comment"
              value={input}
              data-e2e-newnote-input
              onChange={({ target: { value } }) => setInput(value)}
            />
            <PerButtonCircle
              icon="check_stroke"
              variant="pastel"
              color="success"
              ml="xs"
              size="small"
              onClick={handleEditComment}
              style={{ margin: 0 }}
              disabled={!input || (!!input && !input.trim())}
              data-e2e-addnote-button
            />
          </>
        )}
        {user.employeeId === comment.author.id && !!comment.isNewComment && (
          <PerButtonCircle
            icon="close_stroke"
            variant="default"
            color="secondary"
            ml="xs"
            size="small"
            onClick={() => removeComment(comment.isNewComment ? comment.id : comment.globalId, comment.isNewComment)}
            style={{ margin: 0 }}
            data-e2e-addnote-button
          />
        )}
      </PerFlexRow>
    </PerFlexRow>
  )
}
